<template>
  <DatatableReportV3
    v-if="initialData"
    title="Service Utilization"
    download-url="/reports/service_utilization"
    :source="initialData.source"
    :config="config"
    :filters="initialData.filters"
    @filter-applied="onFilterApplied"
    @filter-removed="onFilterRemoved"
    @filters-cleared="onFiltersCleared"
  />
</template>

<script setup lang="ts">
import { onMounted, ref } from "vue";
import { trackEvent } from "@/lib/pendo";

import Badge from "@/components/Badge.vue";
import { Filter } from "@/components/DatatableV3.vue";
import DatatableReportV3 from "@/Reports/DatatableReportV3.vue";

interface InitialPageData {
  source: string;
  filters: Filter[];
}

interface FilterAppliedPayload {
  filter_name: string;
  filter_value: unknown;
  filter_value_count?: number;
}

interface FilterRemovedPayload {
  filter_name: string;
}

interface FiltersClearedPayload {
  filters_cleared_count: number;
}

const initialData = ref<InitialPageData | null>(null);

const onFilterApplied = (payload: FilterAppliedPayload) => {
  trackEvent("service_utilization_filter_applied", payload);
};

const onFilterRemoved = (payload: FilterRemovedPayload) => {
  trackEvent("service_utilization_filter_removed", payload);
};

const onFiltersCleared = (payload: FiltersClearedPayload) => {
  trackEvent("service_utilization_filters_cleared", payload);
};

const config = {
  // Default sort is Available until ascending — shows the most urgent rows
  // first so the admin doesn't have to paginate to triage.
  order: [[1, "asc"]],
  language: {
    emptyTable: "No services available"
  },
  createdRow: (row: HTMLElement, _data: unknown, dataIndex: number) => {
    row.setAttribute("data-cy", `service-utilization-row-${dataIndex}`);
  },
  vueComponents: [{ rootId: "badge-vue", component: Badge }],
  columns: [
    { data: "status", title: "Status", searchable: false },
    {
      data: "available_until",
      title: "Available until",
      searchable: false,
      filterName: "ends_at"
    },
    { data: "customer", title: "Customer" },
    {
      data: "task",
      title: "Task",
      orderable: false,
      searchable: false,
      filterName: "line_item_ids",
      headerClassName: "su-task-filter-column"
    },
    { data: "service_address", title: "Service Address" },
    { data: "zip", title: "Zip" },
    { data: "completion_date", title: "Completion Date", searchable: false },
    {
      data: "invoice",
      title: "Invoice",
      orderable: false,
      searchable: false
    },
    { data: "program", title: "Program" },
    { data: "membership_id", title: "Membership ID" }
  ]
};

onMounted(async () => {
  const res = await fetch(
    `${window.location.origin}/reports/service_utilization/initial_page_data`,
    { method: "GET" }
  );
  initialData.value = await res.json();
});
</script>

<style lang="scss">
// The controller sets autoWidth: false on the line_item_filter for this
// report, so the Select fills its column container instead of measuring
// the longest task label against the viewport. This cap keeps the Task
// column from expanding when a verbose task is selected; the pill chip
// styling itself lives on the shared Select component.
th.su-task-filter-column {
  max-width: 260px;
}
</style>
